import { useEffect, useRef, useState } from 'react'
import { ChevronDown } from 'lucide-react'
import {
  addMonths,
  formatMonthYear,
  monthGrid,
  monthOf,
  startOfWeek,
  today,
} from '../api/calendar'
import type { CalendarView } from './CalendarViewSwitcher'

const WEEKDAY_INITIALS = ['M', 'T', 'W', 'T', 'F', 'S', 'S']
const SHORT_MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
]

/**
 * The views that show a day grid at all.
 *
 * Month view picks a month directly from the month block, so it never renders
 * one — which is why the selection predicates below only know about days and
 * weeks.
 */
export type DayGridView = 'day' | 'week'

/**
 * Does clicking `iso` land on the same thing as `reference`?
 *
 * The unit of selection is the unit of the view: a day view picks a day, a week
 * view picks the week that day belongs to. Both the selected highlight and the
 * hover preview run through this, so what the pointer promises is exactly what
 * the click delivers.
 */
export function sameSelection(iso: string, reference: string, view: DayGridView): boolean {
  return view === 'week' ? startOfWeek(iso) === startOfWeek(reference) : iso === reference
}

/**
 * Round only the ends of a contiguous highlighted run within a grid row.
 *
 * Rounding every cell breaks a selected week into seven separate lozenges
 * instead of one band. Rounding only where the run actually starts and stops
 * gives it a single shape, including where it meets the edge of a row.
 */
export function runRounding(
  grid: string[],
  index: number,
  reference: string,
  view: DayGridView,
): string {
  const col = index % 7
  const joinsLeft = col > 0 && sameSelection(grid[index - 1], reference, view)
  const joinsRight = col < 6 && sameSelection(grid[index + 1], reference, view)
  return `${joinsLeft ? '' : 'rounded-l-md'} ${joinsRight ? '' : 'rounded-r-md'}`
}

function StepButtons({
  onBack,
  onForward,
  backLabel,
  forwardLabel,
}: {
  onBack: () => void
  onForward: () => void
  backLabel: string
  forwardLabel: string
}) {
  const cls =
    'grid size-6 place-items-center rounded text-[13px] text-ink-3 transition-colors hover:bg-surface-2 hover:text-ink'
  return (
    <span className="flex shrink-0 gap-0.5">
      <button onClick={onBack} aria-label={backLabel} className={cls}>
        &uarr;
      </button>
      <button onClick={onForward} aria-label={forwardLabel} className={cls}>
        &darr;
      </button>
    </span>
  )
}

/**
 * Jump-to-date control, opened from the calendar's own title.
 *
 * Paging a week at a time is fine for "next week" and useless for "the week of
 * recital". The title is where the eye already is when the question is *when*,
 * so it doubles as the affordance rather than adding another button to the row.
 *
 * Day and week views put the day grid beside the month block, so a distant
 * month and then a day within it is two clicks in one popover. Month view shows
 * the month block alone — a grid of thirty highlighted days answers a question
 * nobody asked when the thing being chosen is the month.
 */
export default function CalendarDatePicker({
  label,
  anchor,
  view,
  onPick,
}: {
  /** The rendered title, e.g. "August 3-9, 2026". */
  label: string
  /** Date the picker opens on. */
  anchor: string
  /** Controls what a click means and what the hover highlight covers. */
  view: CalendarView
  onPick: (date: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [page, setPage] = useState(() => monthOf(anchor))
  const [hovered, setHovered] = useState<string | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // Reopening after navigating elsewhere should start from where the calendar
  // now is, not wherever the picker was last left.
  useEffect(() => {
    if (open) setPage(monthOf(anchor))
  }, [open, anchor])

  useEffect(() => {
    if (!open) return

    const onPointerDown = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }

    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const choose = (date: string) => {
    setOpen(false)
    setHovered(null)
    onPick(date)
  }

  const now = today()
  const isMonthView = view === 'month'
  const dayView: DayGridView = view === 'week' ? 'week' : 'day'
  const grid = monthGrid(page.year, page.month)
  const pageKey = `${page.year}-${String(page.month).padStart(2, '0')}`

  return (
    // Negative margin cancels the button's own padding, so the title stays
    // flush with the eyebrow above it while still having a hover target.
    <div ref={containerRef} className="relative -ml-2 inline-block">
      <button
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
        className="group flex items-center gap-2 rounded-lg px-2 py-0.5 text-left transition-colors hover:bg-surface-2"
        title="Jump to a date"
      >
        <span className="font-display text-3xl">{label}</span>
        <ChevronDown
          size={20}
          strokeWidth={2.5}
          className="mt-1 shrink-0 text-ink-3 transition-colors group-hover:text-ink-2"
        />
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Jump to a date"
          // Content-sized and absolutely positioned, so it is free to be wider
          // than the title it hangs from.
          /*
           * **Stacked on a phone, side by side once there is room** (D-359).
           *
           * Side by side it measures 280 + 28 + 1 + 28 + 180 and 48 of padding
           * — **579px inside a 390px window**, so it hung 189px off the right
           * edge and made the whole page scroll sideways. Stacked it is 328px
           * and fits.
           *
           * **It had been that way for months and nothing had seen it**, which
           * is the point rather than an aside: `audit.mjs` only ever measures
           * the first screen at a URL, and this opens on a tap. It was found the
           * day the picker was given to a second caller and `audit-deep.mjs`
           * was pointed at it — the same shape as the six kiosk screens in
           * D-254.
           */
          className="absolute left-0 z-30 mt-2 flex flex-col gap-5 rounded-xl border border-line bg-surface p-4 shadow-xl sm:flex-row sm:gap-7 sm:p-6"
          onMouseLeave={() => setHovered(null)}
        >
          {!isMonthView && (
            <>
              {/*
                Pinned to the grid's own width (7 x 2.5rem). Left to size itself
                the column takes the width of its widest child, which is the
                header — so paging from July to September physically widened the
                popover and resized every cell under it.
              */}
              <div className="w-[17.5rem]">
                <div className="mb-3.5 flex items-center justify-between gap-4">
                  <span className="min-w-0 truncate text-[13.5px] font-semibold">
                    {formatMonthYear(page.year, page.month)}
                  </span>
                  <StepButtons
                    onBack={() => setPage(addMonths(page.year, page.month, -1))}
                    onForward={() => setPage(addMonths(page.year, page.month, 1))}
                    backLabel="Previous month"
                    forwardLabel="Next month"
                  />
                </div>

                {/*
                  Explicit 40px tracks, never `grid-cols-7`. That shorthand is
                  `minmax(0, 1fr)`, which lets the columns collapse narrower than
                  the cells; each cell then overflows its track and the next
                  one's background paints over its second digit, so "24 25 26"
                  renders sliced. Fixed tracks also keep the weekday initials
                  centred over their columns.

                  No horizontal gap: a highlighted week has to read as one
                  continuous band, and a gutter breaks it into seven chips.
                  Rounding goes to the ends of the run instead.
                */}
                <div className="grid grid-cols-[repeat(7,2.5rem)] gap-y-1">
                  {WEEKDAY_INITIALS.map((d, i) => (
                    <div
                      key={i}
                      aria-hidden="true"
                      className="pb-2 text-center text-[11px] font-bold tracking-wide text-ink-3"
                    >
                      {d}
                    </div>
                  ))}

                  {grid.map((iso, i) => {
                    const inMonth = iso.slice(0, 7) === pageKey
                    const isSelected = sameSelection(iso, anchor, dayView)
                    const isHovered =
                      hovered !== null && sameSelection(iso, hovered, dayView)
                    const isToday = iso === now

                    // Rounding follows whichever run this cell belongs to.
                    const rounding = isSelected
                      ? runRounding(grid, i, anchor, dayView)
                      : isHovered
                        ? runRounding(grid, i, hovered!, dayView)
                        : 'rounded-md'

                    const tone = isSelected
                      ? 'bg-accent font-semibold text-on-accent'
                      : isHovered
                        ? 'bg-surface-2 text-ink'
                        : isToday
                          ? 'font-bold text-accent'
                          : inMonth
                            ? 'text-ink-2'
                            : // Dimmed, not hidden. text-ink-3 is already low
                              // contrast on the dark ground, so the old 55%
                              // took the neighbouring months to near-invisible.
                              'text-ink-3 opacity-70'

                    return (
                      <button
                        key={iso}
                        onClick={() => choose(iso)}
                        onMouseEnter={() => setHovered(iso)}
                        onFocus={() => setHovered(iso)}
                        aria-current={isSelected ? 'date' : undefined}
                        // `relative` so the focus ring can lift above the
                        // neighbouring cells — z-index is inert on a static box.
                        className={`tabular relative h-10 w-full text-[13px] transition-colors focus-visible:z-10 focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-accent ${rounding} ${tone}`}
                      >
                        {Number(iso.slice(-2))}
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* A vertical rule between two stacked blocks is a hairline in
                  the wrong axis, so it goes when they stack. */}
              <div className="hidden w-px shrink-0 bg-line sm:block" aria-hidden="true" />
            </>
          )}

          {/* Month block. The whole picker in month view; a companion otherwise.
              Width pinned for the same reason as the day column. */}
          <div className={`flex flex-col ${isMonthView ? 'w-[15.125rem]' : 'w-[11.25rem]'}`}>
            <div className="mb-3.5 flex items-center justify-between gap-4">
              <span className="tabular text-[13.5px] font-semibold">{page.year}</span>
              <StepButtons
                onBack={() => setPage({ ...page, year: page.year - 1 })}
                onForward={() => setPage({ ...page, year: page.year + 1 })}
                backLabel="Previous year"
                forwardLabel="Next year"
              />
            </div>

            {/*
              Fixed tracks here too, and for the same reason — 1fr columns let
              "Aug" overflow its cell, which is why the highlight pill sat off
              to one side of its label. Standing alone the block can afford four
              across and stay compact; beside the day grid, three keeps the two
              blocks close in proportion.
            */}
            <div
              className={`grid gap-1.5 ${
                isMonthView ? 'grid-cols-[repeat(4,3.5rem)]' : 'grid-cols-[repeat(3,3.5rem)]'
              }`}
            >
              {SHORT_MONTHS.map((name, i) => {
                const m = i + 1
                const isCurrent = m === page.month
                return (
                  <button
                    key={name}
                    onClick={() =>
                      isMonthView
                        ? choose(`${page.year}-${String(m).padStart(2, '0')}-01`)
                        : setPage({ year: page.year, month: m })
                    }
                    aria-current={isCurrent ? 'true' : undefined}
                    /*
                     * In month view this button *is* the selection, so it takes
                     * the solid accent. Beside a day grid it only reports which
                     * month is on show — a second solid accent there would
                     * compete with the actual selected day or week.
                     */
                    className={`w-full rounded-md py-2.5 text-center text-[13px] transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent ${
                      !isCurrent
                        ? 'text-ink-2 hover:bg-surface-2'
                        : isMonthView
                          ? 'bg-accent font-semibold text-on-accent'
                          : 'bg-accent-soft font-semibold text-accent-2'
                    }`}
                  >
                    {name}
                  </button>
                )
              })}
            </div>

            <button
              onClick={() => choose(now)}
              className="mt-auto self-end rounded-md px-2 pt-5 text-[13px] font-semibold text-accent hover:underline"
            >
              Today
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
